import { ChangeDetectorRef, Component, EventEmitter, OnDestroy, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import { ErweiterteAuftraggeberSucheObservable } from '../../services.module/message.observable';
import { ErweiterteAuftraggeberSucheModel } from '../../model.module/modals.model';
import { SortType } from '../../model.module/constant/sort-types.enum';
import { TableHeader } from '../../model.module/types/table-header.type';
import { Subscription } from 'rxjs';
import { AuftraggeberApiService } from '../../../generated/api/auftraggeber/auftraggeber.service';
import { Auftraggeber, AuftraggeberSucheResponse } from '../../../generated/api/kreditportalBackendAPI.schemas';

type TableAuftraggeber = {
    auftraggeber: string;
    strasse: string;
    plzort: string;
    spKennung?: string;
    vertrittOeffentlicheRessorts: boolean;
};

@Component({
    selector: 'erweiterte-auftraggeber-suche',
    templateUrl: './erweiterte-auftraggeber-suche.component.html',
    styleUrls: ['./erweiterte-auftraggeber-suche.component.less'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ErweiterteAuftraggeberSucheComponent implements OnInit, OnDestroy {

    @Output()
    searchResultSelected = new EventEmitter<Auftraggeber>();

    show = false;

    txtClose = 'Schließen';

    isLoading = false;

    auftraggeberTableHeaders: TableHeader[] = [
        {
            id: "auftraggeber",
            label: "Auftraggeber",
            sortable: true,
            sortType: SortType.ALPHABETICAL
        },
        {
            id: "strasse",
            label: "Straße, Hausnummer",
            sortable: true,
            sortType: SortType.ALPHABETICAL
        },
        {
            id: "plzort",
            label: "PLZ, Ort",
            sortable: true,
            sortType: SortType.ALPHABETICAL
        }
    ];

    auftraggeberTableData: TableAuftraggeber[];

    selectedAuftraggeber: TableAuftraggeber;

    auftraggeberSucheQuery: string;

    plzSucheQuery: string;

    app: string;

    auftraggeberSucheError = false;

    messageHeadline: string;

    messageContent: string;

    private subscription: Subscription;

    constructor(
        private readonly auftraggeberApiService: AuftraggeberApiService,
        private readonly erweiterteAuftraggeberSucheObservable: ErweiterteAuftraggeberSucheObservable,
        private readonly changeDetectorRef: ChangeDetectorRef
    ) {
        this.resetDialog();
    }

    ngOnInit(): void {
        this.subscription = this.erweiterteAuftraggeberSucheObservable.subscribe((message: ErweiterteAuftraggeberSucheModel) => {
            if (!message || message.isHandled) {
                return;
            }

            message.isHandled = true;

            this.resetDialog();

            if (message.sucheVorbelegung) {
                // Führe direkt Suche durch, wenn es eine Vorbelegung gibt
                this.auftraggeberSucheQuery = message.sucheVorbelegung;
                this.onSucheBtnClick();
            }

            this.show = true;
            this.changeDetectorRef.detectChanges();
        });
    }

    ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }

    onInputFieldKeyUp(event: KeyboardEvent) {
        if (event.key === 'Enter') {
            this.onSucheBtnClick();
        }
    }

    onSucheBtnClick() {
        if (!this.auftraggeberSucheQuery) {
            this.auftraggeberSucheError = true;
            this.setMessage('Bitte geben Sie einen Suchbegriff ein.');

            return;
        }

        this.isLoading = true;
        this.selectedAuftraggeber = undefined;
        this.auftraggeberTableData = undefined;
        this.resetMessage();

        this.auftraggeberApiService.auftraggeberSuche({
            name: this.auftraggeberSucheQuery,
            plz: this.plzSucheQuery,
            limit: false
        }).subscribe({
            next: (response: AuftraggeberSucheResponse) => {
                if (!response.auftraggeber || response.auftraggeber.length === 0) {
                    this.setMessage(
                        'Zu den Suchkriterien gibt es keine Ergebnisse.',
                        'Möglicherweise ist Ihr Auftraggeber nicht für die digitale Bürgschaft registriert. ' +
                        'Bitte überprüfen Sie Ihre Angaben oder erfassen Sie die Auftraggeberdaten manuell.'
                    );

                    return;
                }

                this.auftraggeberTableData = response.auftraggeber.map(
                    auftraggeber => ErweiterteAuftraggeberSucheComponent.transformAuftraggeberToTableAuftraggeber(auftraggeber)
                );

                this.isLoading = false;
            },
            error: (err) => {
                console.error(err);
                this.isLoading = false;
            }
        });
    }

    onRowSelected(event: object) {
        this.selectedAuftraggeber = event as TableAuftraggeber;
    }

    onCancelClick() {
        this.close();
    }

    onApplyClick() {
        if (!this.selectedAuftraggeber) {
            console.error('Sanity-Check fehlgeschlagen, kein Auftraggeber ausgewählt.');
            this.close();
            return;
        }

        console.debug('Ausgewählter Auftraggeber:', JSON.stringify(this.selectedAuftraggeber));

        this.searchResultSelected.emit(
            ErweiterteAuftraggeberSucheComponent.transformTableAuftraggeberToAuftraggeber(this.selectedAuftraggeber)
        );

        this.close();
    }

    close(): void {
        this.show = false;
    }

    private resetDialog() {
        this.selectedAuftraggeber = undefined;
        this.auftraggeberTableData = undefined;
        this.auftraggeberSucheQuery = '';
        this.plzSucheQuery = '';
        this.auftraggeberSucheError = false;

        this.setMessage('Um eine Suche durchzuführen, geben Sie bitte in die obigen Eingabefelder Suchbegriffe ein und klicken auf "Suchen".')
    }

    private resetMessage() {
        this.messageHeadline = undefined;
        this.messageContent = undefined;
    }

    private setMessage(headline: string, content: string = undefined) {
        this.messageHeadline = headline;
        this.messageContent = content;
    }

    private static transformAuftraggeberToTableAuftraggeber(auftraggeber: Auftraggeber): TableAuftraggeber {
        return {
            auftraggeber: auftraggeber.name,
            strasse: auftraggeber.strasse,
            plzort: `${auftraggeber.landeskennzeichen}-${auftraggeber.plz} ${auftraggeber.ort}`,
            spKennung: auftraggeber.spKennung,
            vertrittOeffentlicheRessorts: auftraggeber.vertrittOeffentlicheRessorts
        }
    }

    private static transformTableAuftraggeberToAuftraggeber(tableAuftraggeber: TableAuftraggeber): Auftraggeber {
        const plzort = tableAuftraggeber.plzort;
        const landeskennzeichen = plzort.substring(0, plzort.indexOf('-'));
        const plz = plzort.substring(plzort.indexOf('-') + 1, plzort.indexOf(' '));
        const ort = plzort.substring(plzort.indexOf(' ') + 1);

        return {
            spKennung: tableAuftraggeber.spKennung,
            name: tableAuftraggeber.auftraggeber,
            strasse: tableAuftraggeber.strasse,
            plz,
            ort,
            landeskennzeichen,
            vertrittOeffentlicheRessorts: tableAuftraggeber.vertrittOeffentlicheRessorts
        }
    }

}
